import { getAllData } from "apis/customApiHandler";
import useDebounce from "hooks/useDebounce";
import { useState } from "react";
import { useQuery } from "react-query";
import ReactSelect from "react-select";
import { BiCloudDownload } from "react-icons/bi";
import { Button, Input } from "styled";
import { TH } from "styled/table";
import exportExcel from "utils/exportExcel";
import Spinner from "components/Spinner";
import ViewList from "./ViewList";

const FILTERS = [
  { name: "fleetCode", label: "Fleet Code" },
  { name: "fleetName", label: "Fleet Name" },
  { name: "fleetType", label: "Fleet Type" },
  { name: "orderBlock", label: "Order Block" },
  { name: "hubCode", label: "Hub Code" },
  { name: "hubName", label: "Hub Name" },
  { name: "hubStatus", label: "Hub Status" },
  { name: "depoCode", label: "Depot Code" },
  { name: "region", label: "Region" },
  { name: "zone", label: "Zone" },
  { name: "bodyType", label: "Body Type" },
  { name: "hubInCharge", label: "Hub In-Charge" },
  { name: "fleetManager", label: "Fleet Manager" },
];

const initialFilters = {
  fleetCode: "",
  fleetName: "",
  fleetType: "",
  orderBlock: "",
  hubCode: "",
  hubName: "",
  hubStatus: "",
  depoCode: "",
  region: "",
  zone: "",
  bodyType: "",
  hubInCharge: "",
  fleetManager: "",
};

// A person deactivated in Users is still listed here, flagged — the row must not
// go blank just because they were switched off. The table paints them red; the
// export carries the same information as an "(Inactive)" suffix.
const formatPeople = (users, fallback) => {
  if (!users?.length) return fallback || "";
  return users
    .map((user) =>
      user.active === false ? `${user.name} (Inactive)` : user.name
    )
    .join(", ");
};

const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderColor: "transparent",
    minWidth: "100%",
    "&:hover": {
      borderColor: "rgb(34 197 94)",
    },
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
};

const Index = () => {
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState(initialFilters);
  const [downloading, setDownloading] = useState(false);
  const debouncedValue = useDebounce(search, 1000);

  const buildQuery = () => {
    const query = {};
    if (debouncedValue) query.searchQuery = debouncedValue;
    Object.entries(filters).forEach(([key, value]) => {
      if (value) query[key] = value;
    });
    return query;
  };

  const { data, isLoading } = useQuery(
    ["fleet-hub-mapping", debouncedValue, JSON.stringify(filters)],
    () => getAllData("/fleet-hub-mapping", buildQuery())
  );

  const { data: filterOptions } = useQuery(["fleet-hub-mapping-options"], () =>
    getAllData("/fleet-hub-mapping/filter-options")
  );

  const getOptions = (name) => {
    const values = filterOptions?.data?.[name] || [];
    return [
      { label: "All", value: "" },
      ...values.map((value) => ({ label: value, value })),
    ];
  };

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const getDownloadData = async () => {
    setDownloading(true);
    const dt = await getAllData("/fleet-hub-mapping/download", buildQuery());
    if (dt?.data?.length) {
      exportExcel(
        dt.data.map((x, idx) => ({
          "S. No.": idx + 1,
          "Fleet Code": x.fleetCode,
          "Fleet Name": x.fleetName,
          "Hub Code": x.hubCode,
          "Hub Name": x.hubName,
          "Fleet/Hub Opening Date": x.openingDate
            ? new Date(x.openingDate)
                .toLocaleDateString("en-GB")
                .replace(/\//g, "-")
            : "",
          "Depot Code": x.depoCode,
          "CPKM Vehicles": x.cpkmVehicles,
          "NCPKM Vehicles": x.ncpkmVehicles,
          Region: x.region,
          Zone: x.zone,
          "Body Type": x.bodyType,
          "Hub In-Charge / Site In-Charge": formatPeople(
            x.hubInChargeUsers,
            x.hubInCharge
          ),
          "Fleet Manager": formatPeople(x.fleetManagerUsers, x.fleetManager),
          "Cluster Manager": formatPeople(
            x.clusterManagerUsers,
            x.clusterManager
          ),
          "Zonal Manager": formatPeople(x.zmUsers, x.zm),
          "Hub Status": x.hubStatus,
          "Order Block": x.orderBlock,
        })),
        "fleet-hub-mapping-master.xlsx"
      );
    }
    setDownloading(false);
  };

  return (
    <div>
      <div className="m-10">
        <div className="mt-8 flex justify-start gap-6 items-center flex-wrap lg:flex-nowrap">
          <div className="relative w-full">
            <Input
              type="search"
              placeholder="Search Here"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Button
            className="!px-8 disabled:bg-gray-400 whitespace-nowrap"
            bgPrimary
            disabled={downloading}
            onClick={async () => {
              await getDownloadData();
            }}
          >
            <div className="flex gap-1 items-center">
              <BiCloudDownload className="w-5 h-5" />
              {downloading ? "Downloading..." : "Download"}
            </div>
          </Button>
          <Button
            type="button"
            bgSecondary
            className="whitespace-nowrap"
            onClick={() => {
              setFilters(initialFilters);
              setSearch("");
            }}
          >
            Reset Filters
          </Button>
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-x-4">
          {FILTERS.map((filter) => (
            <label key={filter.name} className="w-full py-2">
              {filter.label}
              <ReactSelect
                styles={selectStyles}
                menuPortalTarget={document.body}
                className="shadow-md rounded-md py-1 mb-2"
                options={getOptions(filter.name)}
                value={{
                  value: filters[filter.name],
                  label: filters[filter.name] || "All",
                }}
                onChange={(option) =>
                  handleFilterChange(filter.name, option?.value || "")
                }
                name={filter.name}
              />
            </label>
          ))}
        </div>
      </div>
      {isLoading ? (
        <Spinner />
      ) : (
        <div className="flex flex-col m-2 lg:m-10">
          <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
              <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <TH scope="col">S.N.</TH>
                      <TH scope="col">Fleet Code</TH>
                      <TH scope="col">Fleet Name</TH>
                      <TH scope="col">Hub Code</TH>
                      <TH scope="col">Hub Name</TH>
                      <TH scope="col">Depot Code</TH>
                      <TH scope="col">Region</TH>
                      <TH scope="col">Zone</TH>
                      <TH scope="col">Body Type</TH>
                      <TH scope="col">Fleet/Hub Opening Date</TH>
                      <TH scope="col">Hub In-Charge</TH>
                      <TH scope="col">Fleet Manager</TH>
                      <TH scope="col">Cluster Manager</TH>
                      <TH scope="col">Zonal Manager</TH>
                      <TH scope="col">Hub Status</TH>
                      <TH scope="col">Order Lock</TH>
                      <TH scope="col">Actions</TH>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {data?.data &&
                      data?.data.map((d, index) => (
                        <ViewList
                          data={d}
                          key={d._id}
                          index={index}
                          serial={index + 1}
                        />
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Index;
